import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { ScoreGauge } from "@/components/ScoreGauge";

export function AccountPage(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ["account"],
    queryFn: api.account,
  });

  if (isLoading) return <main className="text-chrome-300">Loading…</main>;
  if (error || !data) return <main className="text-crit">Failed to load.</main>;

  return (
    <main>
      <PageHeader
        eyebrow="subscriber"
        title="Account"
        subtitle="Subscription, consent, and posture overview. MFA management ships in Phase 3."
      />

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="panel p-6">
          <div className="label-overline">household</div>
          <div className="mt-2 text-lg font-semibold text-chrome-50">
            {data.display_name}
          </div>
          <div className="code mt-1 text-[11px] text-chrome-400">{data.id}</div>
        </div>

        <div className="panel p-6">
          <div className="label-overline">subscription</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-lg font-semibold uppercase text-chrome-50">
              {data.subscription.tier}
            </span>
            <span
              className={[
                "rounded border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest",
                data.subscription.status === "active"
                  ? "border-ok/30 bg-ok/10 text-ok-400"
                  : "border-chrome-400/30 bg-chrome-400/10 text-chrome-300",
              ].join(" ")}
            >
              {data.subscription.status}
            </span>
          </div>
          <div className="mt-3 text-[11px] text-chrome-400">
            renews {new Date(data.subscription.renews_at).toLocaleDateString()}
          </div>
        </div>

        <div className="panel p-6">
          <div className="label-overline">consent</div>
          <div className="code mt-2 text-base text-chrome-50">
            {data.consent.version}
          </div>
          <div className="mt-1 text-[11px] text-chrome-400">
            accepted {new Date(data.consent.accepted_at).toLocaleDateString()}
          </div>
        </div>

        <div className="panel p-6">
          <div className="label-overline">current posture score</div>
          <div className="mt-3 flex items-center gap-5">
            <ScoreGauge value={data.current_score.value} size={72} />
            <div>
              <div className="text-cyber-400 text-glow-cyber font-mono text-4xl font-semibold">
                {data.current_score.letter}
              </div>
              <div className="text-xs text-chrome-300 tabular-nums">
                {data.current_score.value}
                <span className="text-chrome-500">/100</span>
              </div>
            </div>
            {typeof data.current_score.delta_30d === "number" && (
              <div className="ml-auto rounded border border-ink-400 bg-ink-600 p-3 text-center">
                <div className="label-overline">30d delta</div>
                <div
                  className={[
                    "mt-1 text-lg font-semibold tabular-nums",
                    data.current_score.delta_30d >= 0
                      ? "text-ok-400"
                      : "text-crit-400",
                  ].join(" ")}
                >
                  {data.current_score.delta_30d >= 0 ? "+" : ""}
                  {data.current_score.delta_30d}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
